import React, { useCallback, useEffect, useState } from "react";
import { Sparkles, X, Check, Ban, ShieldOff, Clock, User as UserIcon } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
};

const typeLabel = (t) => (t === "trader" ? "Trader" : t === "studio" ? "Studio" : t || "—");

/**
 * Gestione richieste/autorizzazioni Magic Search per l'admin.
 * Pulsante con badge (richieste in attesa) + finestra con due sezioni:
 *  - In attesa → Approva / Rifiuta
 *  - Autorizzazioni attive → Revoca (con scadenza per inutilizzo 30 gg)
 */
export default function AdminMagicRequests() {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState([]);
  const [active, setActive] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadPending = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/magic-search-requests?status=pending");
      setPending(data || []);
    } catch (e) { console.error("[magic-requests] pending load failed", e); }
  }, []);

  const loadActive = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/magic-search-requests?status=active");
      setActive(data || []);
    } catch (e) { console.error("[magic-requests] active load failed", e); }
  }, []);

  const reloadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadPending(), loadActive()]);
    setLoading(false);
  }, [loadPending, loadActive]);

  useEffect(() => { loadPending(); }, [loadPending]);
  useEffect(() => { if (open) reloadAll(); }, [open, reloadAll]);

  const approve = async (r) => {
    setBusyId(r.id);
    try {
      await api.put(`/admin/magic-search-requests/${r.id}/approve`);
      toast.success(`Magic Search autorizzata per ${r.studio || r.user_email}`);
      await reloadAll();
    } catch { toast.error("Errore approvazione"); }
    finally { setBusyId(null); }
  };

  const reject = async (r) => {
    if (!window.confirm(`Rifiutare la richiesta di ${r.studio || r.user_email}?`)) return;
    setBusyId(r.id);
    try {
      await api.delete(`/admin/magic-search-requests/${r.id}`);
      await reloadAll();
    } catch { toast.error("Errore"); }
    finally { setBusyId(null); }
  };

  const revoke = async (r) => {
    if (!window.confirm(`Revocare l'accesso Magic Search a ${r.studio || r.user_email}?`)) return;
    setBusyId(r.id);
    try {
      await api.post(`/admin/magic-search-requests/${r.id}/revoke`);
      toast.success("Accesso revocato");
      await reloadAll();
    } catch { toast.error("Errore revoca"); }
    finally { setBusyId(null); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`relative inline-flex items-center gap-2 px-4 py-2 rounded-sm text-[11px] tracking-[0.2em] uppercase transition-colors ${pending.length > 0 ? "bg-gold text-cream" : "border border-ink/20 text-slate1 hover:bg-ink hover:text-cream hover:border-ink"}`}
        data-testid="magic-requests-btn"
      >
        <Sparkles size={14} /> Richieste Magic Search
        {pending.length > 0 && (
          <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 bg-cream text-ink text-[10px] rounded-full">
            {pending.length}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[110] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
          onClick={() => setOpen(false)}
          data-testid="magic-requests-modal"
        >
          <div className="bg-cream w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-sm" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 bg-ink text-cream flex items-center justify-between sticky top-0 z-10">
              <div className="flex items-center gap-2">
                <Sparkles size={18} className="text-gold" />
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-cream/60">Magic Search</div>
                  <div className="font-display text-lg">Richieste & autorizzazioni</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="w-9 h-9 flex items-center justify-center hover:bg-cream/10 rounded-sm" aria-label="Chiudi" data-testid="magic-requests-close">
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* In attesa */}
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-gold mb-3">In attesa di approvazione ({pending.length})</div>
                {pending.length === 0 && (
                  <div className="text-center text-slate1 py-6 text-sm border border-dashed border-ink/15 rounded-sm">Nessuna richiesta in sospeso.</div>
                )}
                <div className="space-y-3">
                  {pending.map((r) => (
                    <div key={r.id} className="bg-white border border-ink/10 rounded-sm p-4" data-testid={`magic-req-${r.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{typeLabel(r.account_type)}</div>
                          <div className="font-display text-base text-ink truncate">{r.studio || r.user_name || r.user_email}</div>
                          <div className="text-xs text-slate1 truncate">{r.user_email}</div>
                          <div className="text-[11px] text-slate1 mt-1 inline-flex items-center gap-1"><Clock size={11} /> Richiesta {fmtDate(r.requested_at)}</div>
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                          <button
                            onClick={() => approve(r)}
                            disabled={busyId === r.id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-ink text-cream text-[10px] tracking-[0.15em] uppercase rounded-sm hover:bg-ink/90 disabled:opacity-50"
                            data-testid={`magic-req-approve-${r.id}`}
                          >
                            <Check size={13} /> Approva
                          </button>
                          <button
                            onClick={() => reject(r)}
                            disabled={busyId === r.id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-rose-300 text-rose-700 text-[10px] tracking-[0.15em] uppercase rounded-sm hover:bg-rose-50 disabled:opacity-50"
                            data-testid={`magic-req-reject-${r.id}`}
                          >
                            <Ban size={13} /> Rifiuta
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Autorizzazioni attive */}
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-slate1 mb-3">Autorizzazioni attive ({active.length})</div>
                {active.length === 0 && (
                  <div className="text-center text-slate1 py-6 text-sm border border-dashed border-ink/15 rounded-sm">Nessuna autorizzazione attiva.</div>
                )}
                <div className="space-y-3">
                  {active.map((r) => {
                    const expired = r.effective_status === "expired";
                    return (
                      <div key={r.id} className="bg-white border border-ink/10 rounded-sm p-4" data-testid={`magic-active-${r.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 inline-flex items-center gap-1.5">
                              <UserIcon size={11} /> {typeLabel(r.account_type)}
                            </div>
                            <div className="font-display text-base text-ink truncate">{r.studio || r.user_name || r.user_email}</div>
                            <div className="text-xs text-slate1 truncate">{r.user_email}</div>
                            <div className="text-[11px] mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                              <span className="text-slate1">Ultimo uso: <span className="text-ink">{fmtDate(r.last_used_at)}</span></span>
                              {expired ? (
                                <span className="text-rose-700 font-medium">Scaduta (inutilizzo)</span>
                              ) : (
                                <span className="text-emerald-700">Scade {fmtDate(r.expires_at)}{typeof r.days_left === "number" ? ` · ${r.days_left}g` : ""}</span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => revoke(r)}
                            disabled={busyId === r.id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-rose-300 text-rose-700 text-[10px] tracking-[0.15em] uppercase rounded-sm hover:bg-rose-50 disabled:opacity-50 shrink-0"
                            data-testid={`magic-active-revoke-${r.id}`}
                          >
                            <ShieldOff size={13} /> Revoca
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {loading && <div className="text-center text-slate1 text-xs">Aggiornamento…</div>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
